#import "./fragments/aiChatFragments.graphql"
#import "./fragments/aiChatDiagnosticsFragments.graphql"

query getTranscriptList($min: String, $max: String) {
  result: getTranscriptList(min: $min, max: $max) {
    content
    success
  }
}

query getAIChats {
  chats: getAIChats {
    id
    title
    createdAt
    updatedAt
  }
}

query getAIChatById($id: String!) {
  chat: getAIChatById(id: $id) {
    id
    title
    createdAt
    updatedAt
    messages {
      ...AIChatMessageFragment
    }
  }
}

query findAIChatById($id: String!) {
  chat: findAIChatById(id: $id) {
    id
    participantId
    userChatId
    title
    createdAt
    updatedAt
    messages {
      ...AIChatSupportDiagnosticsFragment
    }
    subAgentChats {
      id
      userChatId
      title
      createdAt
      updatedAt
      messages {
        ...AIChatSupportDiagnosticsFragment
      }
    }
    turns {
      id
      aiChatId
      userMessageId
      assistantMessageId
      traceId
      status
      createdAt
      endedAt
    }
    dashboards {
      id
      aiChatId
      title
      createdAt
      isHidden
      versionNumber
      sourceId
      sourceType
    }
  }
}

query listGeneratedAIDashboardsForSupport {
  dashboards: listGeneratedAIDashboardsForSupport {
    id
    title
    aiChatId
    chatTitle
    createdAt
    isHidden
  }
}

query getAIDashboardForSupport($id: ID!) {
  dashboard: getAIDashboardForSupport(id: $id) {
    id
    aiChatId
    title
    createdAt
    isHidden
    format
    content
    chatTitle
    fetchFailed
    toolCalls {
      id
      name
      status
      error
      toolCalledAt
      toolResultAt
      durationMs
      fetchFailed
    }
  }
}

query getAIChatTrace($chatId: ID!) {
  trace: getAIChatTrace(chatId: $chatId) {
    chatId
    traceIds
    spans {
      id
      traceId
      spanId
      parentSpanId
      chatId
      messageId
      source
      kind
      name
      status
      startedAt
      endedAt
      durationMs
      firstEventMs
      firstVisibleOutputMs
      attributes
      error
    }
  }
}

query getAIDashboards {
  dashboards: getAIDashboards {
    id
    title
    aiChatId
    createdAt
    updatedAt
    format
  }
}

query getAIDashboardsDetails {
  dashboards: getAIDashboards {
    id
    isPinned
    sourceLibraryItemId
    categories
  }
}

mutation updateAIDashboard($input: UpdateAIDashboardInput!) {
  updateAIDashboard(input: $input) {
    id
    title
    isHidden
  }
}

mutation upsertAIDashboard($input: UpsertAIDashboardInput) {
  upsertAIDashboard(input: $input) {
    id
    title
    latestVersionNumber
  }
}

fragment DashboardLibraryItemFragment on DashboardLibraryItem {
  id
  title
  description
  prompt
  previewKind
  categories
  visibility
  sharedWithEmails
  seedKey
  isSeeded
  canEdit
  canRemove
  canReview
  isAddedToMine
  canAddToMine
  canClone
  scopeFields {
    key
    label
    type
    placeholder
    fallback
  }
}

query getDashboardLibrary {
  library: getDashboardLibrary {
    canPublishCompany
    mine {
      ...DashboardLibraryItemFragment
    }
    sharedWithMe {
      ...DashboardLibraryItemFragment
    }
    company {
      ...DashboardLibraryItemFragment
    }
    suggested {
      ...DashboardLibraryItemFragment
    }
  }
}

mutation upsertDashboardLibraryItem($input: UpsertDashboardLibraryItemInput!) {
  upsertDashboardLibraryItem(input: $input) {
    ...DashboardLibraryItemFragment
  }
}

mutation reviewDashboardLibrarySuggestion($id: ID!, $accept: Boolean!) {
  reviewDashboardLibrarySuggestion(id: $id, accept: $accept) {
    ...DashboardLibraryItemFragment
  }
}

mutation deleteDashboardLibraryItem($id: ID!) {
  deleteDashboardLibraryItem(id: $id) {
    deleted
  }
}

mutation addDashboardLibraryItemToMine($id: ID!) {
  addDashboardLibraryItemToMine(id: $id) {
    ...DashboardLibraryItemFragment
  }
}

mutation removeDashboardLibraryItemFromMine($id: ID!) {
  removeDashboardLibraryItemFromMine(id: $id) {
    ...DashboardLibraryItemFragment
  }
}

mutation cloneDashboardLibraryItem($id: ID!) {
  cloneDashboardLibraryItem(id: $id) {
    ...DashboardLibraryItemFragment
  }
}

query getAIDashboardById($id: String!, $version: String) {
  dashboard: getAIDashboardById(id: $id, version: $version) {
    id
    title
    aiChatId
    createdAt
    updatedAt
    content
    versionNumber
    latestVersionNumber
    lastPublishedVersionNumber
    lastPublishedAt
    css
    template
    queries
    queriesJson
    variables
    plan
    transforms
    charts
    filters
  }
}

query getAIDashboardGeneratedAt($id: String!, $version: String) {
  dashboard: getAIDashboardById(id: $id, version: $version) {
    id
    generatedAt
  }
}

query hydrateDashboard($input: HydrateDashboardInput!) {
  hydrateDashboard(input: $input) {
    state
    charts
    errors {
      scope
      key
      code
      message
    }
    queryStatus
    queryData
    debug
  }
}

query getAISummaries($searchTerm: String, $limit: Int, $offset: Int) {
  getAISummaries(searchTerm: $searchTerm, limit: $limit, offset: $offset) {
    summaries {
      id
      entityType
      entityId
      status
      error
      createdAt
      updatedAt
      lastRefreshedAt
    }
    total
  }
}

query searchAIChats($searchTerm: String, $limit: Int, $offset: Int) {
  searchAIChats(searchTerm: $searchTerm, limit: $limit, offset: $offset) {
    chats {
      id
      participantId
      title
      createdAt
      updatedAt
    }
    total
  }
}

query getAISummaryById($id: String!) {
  getAISummaryById(id: $id) {
    id
    entityType
    entityId
    systemPrompt
    userPrompt
    content
    citations
    lastRefreshedAt
    status
    error
    createdAt
    updatedAt
  }
}
